// Who the signed-in person is, as the UI presents them.
//
// The header used to show the raw role string in brackets after the name
// ("Matt Smith (admin · partner · finance · hr)"). Roles are how the system decides what
// you may open; they are not who you are, and four of them pushed the name around on every
// screen. The brackets now carry the job title, and the roles moved into the account
// dropdown, where they are still there to be read.
//
// These live outside App.jsx so the fallbacks are testable: plenty of people records have
// no job title recorded, and a role key can exist in PERMISSIONS before anyone gives it a
// label here.

// With the extension, so node --test can import this module as well as Vite.
import { parseRoles } from './rbac.js';
import { initials } from './format.js';

/** Human labels for the role keys used in people.user_role. */
export const ROLE_LABELS = {
  admin:                'Admin',
  partner:              'Partner',
  'commercial-lead':    'Commercial Lead',
  pm:                   'Project Manager',
  pmo:                  'PMO',
  bd:                   'Business Dev',
  finance:              'Finance',
  billing:              'Billing',
  hr:                   'HR',
  middle_manager:       'Middle Manager',
  engineer:             'Engineer',
  contractor:           'Contractor',
  'inventory-manager':  'Inventory Manager',
  qms_manager:          'QMS Manager',
  client:               'Client',
  // Derived from teams.lead_id, never assigned; labelled so the Access screen can say why.
  'team-lead':          'Team Lead',
};

/** One role key to its label, falling back to the key so unlabelled access still shows. */
export function roleLabel(key) {
  return ROLE_LABELS[key] || key;
}

/** A user_role string to the labels it holds, in the order they are held. */
export function roleLabels(roleString) {
  return parseRoles(roleString).map(roleLabel);
}

/** The roles as one line for the account dropdown, e.g. "Admin · Finance · HR". */
export function userAccessLine(roleString) {
  return roleLabels(roleString).join(' · ');
}

/** Initials for the avatar: from the name, else the email, else a placeholder. */
export function userInitials(user) {
  const name = [user?.first_name, user?.last_name].map(p => String(p || '').trim()).filter(Boolean).join(' ');
  if (name) return initials(name);
  const email = String(user?.email || user?.email_work || '').trim();
  return email ? email[0].toUpperCase() : '?';
}

// The people record calls a job title "designation" — a managed dropdown since migration
// 0265. There is a job_title column on the platform, but it belongs to crm_contacts, so
// reading that here would show nothing for everybody.
const jobTitle = (user) => String(user?.designation || '').trim();
const team = (user) => String(user?.team_name || '').trim();

/** The line under the name in the header: job title, else the team, else nothing at all. */
export function userJobLine(user) {
  return jobTitle(user) || team(user) || '';
}

/**
 * The same line with more room, as the dropdown has: job title and team together when both
 * are recorded, and whichever one there is when only one is.
 */
export function userDetailLine(user) {
  return [jobTitle(user), team(user)].filter(Boolean).join(' · ');
}
